import axios from 'axios';
import { API_BASE_URL, API_PREFIX } from './config';

const DEFAULT_TIMEOUT = 60000;

class ApiClient {
  constructor(config = {}) {
    this.baseURL = config.baseURL || API_BASE_URL;
    this.timeout = config.timeout || DEFAULT_TIMEOUT;
    this.isAdmin = config.isAdmin || false;
    this.apiPrefix = config.apiPrefix || API_PREFIX;
    
    this.api = axios.create({
      baseURL: this.baseURL + (config.usePrefix !== false ? this.apiPrefix : ''),
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers
      }
    });
    
    this.setupInterceptors();
  }
  
  setupInterceptors() {
    this.api.interceptors.request.use(
      (config) => this.requestInterceptor(config),
      (error) => Promise.reject(error)
    );
    
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => this.responseInterceptor(error)
    );
  }
  
  requestInterceptor(config) {
    const token = this.isAdmin 
      ? localStorage.getItem('admin_token')
      : localStorage.getItem('access_token') || localStorage.getItem('token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    const userKey = this.isAdmin ? 'admin_user' : 'user';
    const userData = localStorage.getItem(userKey);
    
    if (userData) {
      try {
        const user = JSON.parse(userData);
        const userIdKey = this.isAdmin ? 'X-User-ID' : 'X-User-Id';
        if (user.id) {
          config.headers[userIdKey] = user.id.toString();
        }
      } catch (e) {
      }
    }
    
    return config;
  }
  
  async responseInterceptor(error) {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isLoginOrRegister = url.includes('/auth/login') || url.includes('/auth/register');
      
      if (isLoginOrRegister) {
        return Promise.reject(error);
      }
      
      if (this.isAdmin) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        
        if (!window.location.pathname.startsWith('/sys-portal/login')) {
          window.location.href = '/sys-portal/login';
        }
        return Promise.reject(error);
      }
      
      const { handleAxiosAuthError } = await import('./authErrorHandler');
      return handleAxiosAuthError(error);
    }
    
    return Promise.reject(error);
  }
  
  get(url, config) {
    return this.api.get(url, config);
  }
  
  post(url, data, config) {
    return this.api.post(url, data, config);
  }
  
  put(url, data, config) {
    return this.api.put(url, data, config);
  }
  
  patch(url, data, config) {
    return this.api.patch(url, data, config);
  }
  
  delete(url, config) {
    return this.api.delete(url, config);
  }
}

const userApiClient = new ApiClient({ isAdmin: false });
const adminApiClient = new ApiClient({ isAdmin: true, usePrefix: false });

export { ApiClient, userApiClient, adminApiClient };
export default userApiClient;
